import React from 'react'
import { openLinkInTab } from './openLinkInTab'

/**
 * Render text with clickable links
 * Supports both HTML links and structured link objects
 * @param {string} text - Text content
 * @param {Array} links - Array of link objects with startIndex, length, url, text
 * @returns {React.Element|string} Rendered text with clickable links
 */
export const renderTextWithLinks = (text, links) => {
  if (!text) return ''

  // Check if text already contains HTML with links
  if (text.includes('<a ') && text.includes('</a>')) {
    // Text already contains HTML with links, process them to make them clickable
    const processedHtml = text
      .replace(/<a\s+([^>]*?)>/gi, (match, attributes) => {
        // Extract href from attributes
        const hrefMatch = attributes.match(/href="([^"]*)"/i)
        if (hrefMatch) {
          const url = hrefMatch[1]
          return `<span style="color: #bf9000; cursor: pointer;" onclick="window.open('${url}', '_blank')">`
        }
        return match
      })
      .replace(/<\/a>/gi, '</span>')

    return <span dangerouslySetInnerHTML={{ __html: processedHtml }} />
  }

  // If no links array provided, return plain text
  if (!links || !Array.isArray(links) || links.length === 0) {
    return text
  }

  // Filter links that have position information
  const linksWithPos = links.filter(
    (l) =>
      typeof l.startIndex === 'number' &&
      typeof l.length === 'number' &&
      l.length > 0,
  )

  if (linksWithPos.length === 0) {
    return text
  }

  // Sort links by start position
  const sorted = [...linksWithPos].sort(
    (a, b) => (a.startIndex || 0) - (b.startIndex || 0),
  )

  let result = []
  let cursor = 0

  sorted.forEach((link) => {
    const start = Math.max(0, Math.min(text.length, link.startIndex))
    const end = Math.max(start, Math.min(text.length, start + link.length))

    // Add text before the link
    if (start > cursor) {
      result.push(text.slice(cursor, start))
    }

    // Add the clickable link
    const linkText = text.slice(start, end) || link.text || ''
    if (link.url && linkText) {
      result.push(
        <span
          key={`link-${start}-${end}`}
          style={{
            color: '#bf9000',
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
          onClick={() => openLinkInTab(link.url, '_blank')}>
          {linkText}
        </span>,
      )
    } else {
      result.push(linkText)
    }

    cursor = end
  })

  // Add remaining text after the last link
  if (cursor < text.length) {
    result.push(text.slice(cursor))
  }

  return result
}
